import React, { useState, Dispatch, SetStateAction } from 'react';

import { Button, Modal, ListGroup, InputGroup, FormControl } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useUpdateEffect } from 'react-use';
import { boolean } from 'boolean';

import { useAppSelector } from 'hooks';
import { BellIcon, DeleteIcon } from 'assets/icons';
import { Dropdown } from 'components/Forms';
import { getOperator } from 'constants/constants';
import { OperatorCode } from 'constants/enums';
import { getRangeColorRefRanges } from 'utils/Icons';
import {
  getPrefUnitForMeasurementClass,
  buildChoicesSelectOptions,
  getFormattedValueForMeasurementClass,
  getUnitForMeasurementClass,
  buildNonCompositeClassOptions
} from 'utils/MeasurementsStoreFunctions';
import { useAxios } from 'configs/requests';
import { getGroupByGid } from 'store/groups/reducer';
import { Error, Loading } from 'components/Fetch';
import { getMeasurementsOptions } from 'utils/Measurements';

interface IAddReferenceRangeProps {
  setReferenceRanges: Dispatch<SetStateAction<ICarePlanReferenceRange[]>>;
  setModal: (show: boolean) => void;
  show: boolean;
  referenceRange?: ICarePlanReferenceRange;
  ignoreClasses?: string[];
  carePlanTemplate?: ICarePlanTemplate;
  getCarePlan: any;
}

const AddEditReferenceRange = ({
  setReferenceRanges,
  setModal,
  show,
  referenceRange,
  ignoreClasses,
  carePlanTemplate,
  getCarePlan
}: IAddReferenceRangeProps) => {
  const intl = useIntl();
  const [errors, setErrors] = useState<string[]>([]);
  const patient = useAppSelector(({ patient }) => patient?.data);
  const group = useAppSelector((state) => getGroupByGid(state, patient?.subgroup_gid || ''));
  const defaultState = {
    measurement_class: referenceRange?.measurement_class || '',
    zones: referenceRange ? [...referenceRange.zones].reverse() : []
  };
  const [state, setState] = useState<ICarePlanAddReferenceRange>(defaultState);
  const settings = useAppSelector((state) => state.settings);
  const citc = settings.citc;
  const measurementClasses = citc?.measurementClasses.filter((mClass) => !mClass.composite) || [];
  const measurementClassOptions = buildNonCompositeClassOptions();
  const prefUnit = getPrefUnitForMeasurementClass(state.measurement_class);
  const measurementLimit =
    measurementClasses
      .find((x) => x.className === state.measurement_class)
      ?.units?.find(
        (unitDetail: { [key: string]: IMeasurementUnitDetails }) => unitDetail[prefUnit]
      )?.[prefUnit] || null;

  const [{ loading, error }, post] = useAxios(
    {
      url: carePlanTemplate
        ? '/clinician/2.0.0/CarePlanTemplate'
        : `/clinician/Patient/${patient?.uid}/CarePlan`,
      method: 'POST'
    },
    { manual: true }
  );

  const measurementClassesOptions: ISelectOption[] = [];
  measurementClasses?.forEach((x) => {
    if (!ignoreClasses?.includes(x.className)) {
      measurementClassesOptions.push({
        label: intl.formatMessage({ id: x.className }),
        value: x.className as string
      });
    }
  });

  measurementClassesOptions?.sort((a, b) => {
    if (a.label < b.label) return -1;
    if (a.label > b.label) return 1;
    return 0;
  });

  const Segments = {
    CRITICAL_HIGH: {
      label: 'criticalHighKey',
      color: 'categoryRed',
      low_operator: OperatorCode.GREATER_THAN,
      high_operator: null,
      alert_level: 50,
      alert_enabled: true,
      high: null,
      low: null
    },
    SIGNIFICANTLY_HIGH: {
      label: 'significantlyHighKey',
      color: 'categoryOrange',
      low_operator: OperatorCode.GREATER_THAN,
      high_operator: OperatorCode.LESS_THAN_OR_EQUAL,
      alert_level: 30,
      alert_enabled: true,
      high: null,
      low: null
    },
    HIGH: {
      label: 'highKey',
      color: 'categoryYellow',
      low_operator: OperatorCode.GREATER_THAN,
      high_operator: OperatorCode.LESS_THAN_OR_EQUAL,
      alert_level: 20,
      alert_enabled: false,
      high: null,
      low: null
    },
    NORMAL: {
      label: 'normalKey',
      color: 'categoryGreen',
      low_operator: OperatorCode.GREATER_THAN_OR_EQUAL,
      high_operator: OperatorCode.LESS_THAN_OR_EQUAL,
      alert_level: 10,
      alert_enabled: false,
      high: null,
      low: null
    },
    LOW: {
      label: 'lowKey',
      color: 'categoryYellow',
      low_operator: OperatorCode.GREATER_THAN_OR_EQUAL,
      high_operator: OperatorCode.LESS_THAN,
      alert_level: 20,
      alert_enabled: false,
      high: null,
      low: null
    },
    SIGNIFICANTLY_LOW: {
      label: 'significantlyLowKey',
      color: 'categoryOrange',
      low_operator: OperatorCode.GREATER_THAN_OR_EQUAL,
      high_operator: OperatorCode.LESS_THAN,
      alert_level: 30,
      alert_enabled: true,
      high: null,
      low: null
    },
    CRITICAL_LOW: {
      label: 'criticalLowKey',
      operator: null,
      color: 'categoryRed',
      low_operator: null,
      high_operator: OperatorCode.LESS_THAN,
      alert_level: 50,
      alert_enabled: true,
      high: null,
      low: null
    }
  };

  const getDefaultSegments = (measurementClass: string) => {
    const currentUnit = getPrefUnitForMeasurementClass(measurementClass);
    return Object.values(Segments).map((segment) => {
      return {
        ...segment,
        unit: currentUnit
      };
    });
  };

  const handleMeasurement = (option: ISelectOption) => {
    setState({ measurement_class: option.value, zones: getDefaultSegments(option.value) });
    setErrors([]);
  };

  const deleteZone = (label: string) => {
    const newArray = [...state.zones].filter((x) => x.label !== label);
    setState({
      ...state,
      zones: newArray.map((x, i) => {
        const previousLow = i > 0 ? newArray[i - 1].low : null;
        if (i === 0) {
          return { ...x, high_operator: null, high: null };
        }
        if (i === newArray.length - 1) {
          return { ...x, low_operator: null, low: null, high: previousLow };
        }
        return { ...x, high: previousLow };
      })
    });
  };

  const isValidValue = (newZones: ICarePlanReferenceRangeZone[], label: string, value: number) => {
    const zoneIndex = newZones.findIndex((x) => x.label === label);
    const lowerZone = newZones.find((x, i) => x.label !== label && i > zoneIndex && x.low) || null;
    const higherZone =
      [...newZones]
        .reverse()
        .find(
          (x, i) =>
            x.label !== label &&
            i > [...newZones].reverse().findIndex((x) => x.label === label) &&
            x.low
        ) || null;
    if (!value) return true;
    if (
      measurementClassOptions[state.measurement_class] ||
      isUnder(value, measurementLimit?.min, measurementLimit?.max)
    ) {
      if (lowerZone?.low && Number(lowerZone.low) >= value) {
        return false;
      }
      if (higherZone?.low && Number(higherZone.low) <= value) {
        return false;
      }
      return true;
    } else {
      return false;
    }
  };

  const handleValue = (value: string, label: string) => {
    const lowerIndex = state.zones.findIndex((x) => x.label === label) + 1;
    const newZones = state.zones.map((x, i) => {
      if (x.label === label) return { ...x, low: value ? Number(value) : null };
      if (lowerIndex === i) return { ...x, high: value ? Number(value) : null };
      return x;
    });
    setState({
      ...state,
      zones: newZones
    });
    validate(newZones);
  };

  const toggleAlert = (label: string) => {
    setState({
      ...state,
      zones: state.zones.map((x) => {
        if (x.label === label) return { ...x, alert_enabled: !boolean(x.alert_enabled) };
        return x;
      })
    });
  };

  const handleReset = () => {
    setState({
      measurement_class: state.measurement_class || '',
      zones: getDefaultSegments(state.measurement_class)
    });
    setErrors([]);
  };

  const isUnder = (value: number | number, high?: string | number, low?: string | number) => {
    return Number(value) >= Number(high) && Number(value) <= Number(low) ? true : false;
  };

  const areZonesEqual = (
    zoneA: ICarePlanReferenceRangeZone,
    zoneB: ICarePlanReferenceRangeZone
  ) => {
    const parsedLowForZoneA =
      zoneA.low && typeof zoneA.low === 'string'
        ? parseFloat(zoneA.low)
        : typeof zoneA.low === 'number'
          ? zoneA.low
          : null;
    const parsedLowForZoneB =
      zoneB.low && typeof zoneB.low === 'string'
        ? parseFloat(zoneB.low)
        : typeof zoneB.low === 'number'
          ? zoneB.low
          : null;
    const parsedHighForZoneA =
      zoneA.high && typeof zoneA.high === 'string'
        ? parseFloat(zoneA.high)
        : typeof zoneA.high === 'number'
          ? zoneA.high
          : null;
    const parsedHighForZoneB =
      zoneB.high && typeof zoneB.high === 'string'
        ? parseFloat(zoneB.high)
        : typeof zoneB.high === 'number'
          ? zoneB.high
          : null;
    return (
      parsedLowForZoneA === parsedLowForZoneB &&
      parsedHighForZoneA === parsedHighForZoneB &&
      boolean(zoneA.alert_enabled) === boolean(zoneB.alert_enabled)
    );
  };

  const isUpdatedRangeSameAsOriginal = (
    updatedRangeZones: ICarePlanReferenceRangeZone[],
    originalRangeZones: ICarePlanReferenceRangeZone[]
  ) => {
    if (originalRangeZones.length !== updatedRangeZones.length) return false;
    for (let i = 0; i < updatedRangeZones.length; i++) {
      const zone = updatedRangeZones[i];
      const originalZone = originalRangeZones.find((x) => x.label === zone.label);
      if (!originalZone || !areZonesEqual(originalZone, zone)) {
        return false;
      }
    }
    return true;
  };

  const isSubmittable =
    !errors.length &&
    state.zones.filter((x) => x.low).length === state.zones.length - 1 &&
    !isUpdatedRangeSameAsOriginal(state.zones, referenceRange?.zones || []);

  const validate = (newZones: ICarePlanReferenceRangeZone[]) => {
    const newErrors: string[] = [];
    newZones.forEach((zone) => {
      if (!isValidValue(newZones, zone.label, Number(zone.low))) newErrors.push(zone.label);
    });
    setErrors(newErrors);
  };

  const hasMeasurementChoices = () => {
    return measurementClassOptions[state.measurement_class] ? true : false;
  };

  const handleSubmit = async () => {
    const newData = {
      edit_status: referenceRange ? 'updated' : 'new',
      source: group?.name,
      measurement_class: state.measurement_class,
      zones: [...state.zones].reverse(),
      reference_range_id: referenceRange?.reference_range_id
    };
    await post({
      data: {
        referenceRanges: [newData],
        carePlanTemplate
      }
    });
    setModal(false);
    getCarePlan();
  };

  useUpdateEffect(() => {
    if (!show) setState(defaultState);
  }, [show]);

  useUpdateEffect(() => setState(defaultState), [referenceRange]);

  return (
    <>
      <Modal
        data-test="careplan-reference-ranges-modal"
        show={show}
        onHide={() => setModal(false)}
        backdrop="static"
      >
        <Modal.Header closeButton className="align-items-center">
          <Modal.Title>{intl.formatMessage({ id: 'referenceRangesKey' })}</Modal.Title>
          {referenceRange && state.measurement_class && (
            <div
              data-test="careplan-reference-range-measurement-class"
              data-test-value={state.measurement_class}
              className="text-white ms-3"
            >
              {intl.formatMessage({ id: state.measurement_class })}
            </div>
          )}
        </Modal.Header>
        <Modal.Body>
          {!referenceRange && (
            <Dropdown
              dataTestId="careplan-reference-ranges-class-select"
              options={measurementClassesOptions}
              variant="form"
              onChange={handleMeasurement}
            />
          )}
          {state.measurement_class && (
            <>
              <ListGroup data-test="reference-range-zones" variant="flush" className="ranger mt-4">
                {state.zones.map((zone, key) => (
                  <ListGroup.Item
                    key={key}
                    data-test-value={zone.label}
                    data-test={`reference-range-zone-${zone.label}`}
                  >
                    <div className="d-flex align-items-center">
                      <div className="me-auto d-flex align-items-center">
                        <div
                          data-test={`reference-range-color-${zone.label}`}
                          className={
                            'me-3 level cursor-pointer ' + getRangeColorRefRanges(zone.color)
                          }
                          onClick={() => toggleAlert(zone.label)}
                        >
                          {boolean(zone.alert_enabled) && (
                            <BellIcon
                              data-test={`reference-range-alert-${zone.label}`}
                              className="icon text-white align-middle"
                            />
                          )}
                        </div>
                        <div data-test={`reference-range-label-${zone.label}`}>
                          {intl.formatMessage({ id: zone.label })}
                        </div>
                      </div>
                      <div className="text-right font-weight-bolder d-flex align-items-center">
                        {state.zones.length - 1 > key && (
                          <InputGroup data-test={`reference-range-input-group-${zone.label}`}>
                            <InputGroup.Text data-test={`reference-range-operator-${zone.label}`}>
                              {zone.low_operator && getOperator(zone.low_operator)}
                            </InputGroup.Text>
                            {hasMeasurementChoices() ? (
                              <Dropdown
                                variant="form"
                                options={buildChoicesSelectOptions(state.measurement_class, true)}
                                minWidth={175}
                                isSearchable={false}
                                value={
                                  zone.low !== null
                                    ? buildChoicesSelectOptions(state.measurement_class, true).find(
                                        (choice) => choice.value === zone.low?.toString()
                                      )
                                    : null
                                }
                                onChange={(option: ISelectOption) =>
                                  handleValue(option.value, zone.label)
                                }
                              />
                            ) : (
                              <FormControl
                                data-test={`reference-range-input-${zone.label}`}
                                className="mw-110"
                                isInvalid={errors.includes(zone.label)}
                                onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                                  handleValue(evt.target.value, zone.label)
                                }
                                value={zone.low?.toString() || ''}
                                type="number"
                                min={0}
                              />
                            )}
                            <InputGroup.Text>
                              {getUnitForMeasurementClass(state.measurement_class, zone.unit, intl)}
                            </InputGroup.Text>
                          </InputGroup>
                        )}
                        {state.zones.length > 2 && (
                          <DeleteIcon
                            data-test={`reference-range-delete-${zone.label}`}
                            onClick={() => deleteZone(zone.label)}
                            className="icon text-secondary ms-3 cursor-pointer"
                          />
                        )}
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
              {measurementLimit && (
                <div className="mt-2 mb-2 text-center">
                  Min:
                  <b
                    data-test={`reference-range-min-${state.measurement_class}`}
                    className="ms-2 me-2"
                  >
                    {getFormattedValueForMeasurementClass(
                      measurementLimit?.min,
                      state.measurement_class
                    )}{' '}
                    {prefUnit &&
                      getUnitForMeasurementClass(state.measurement_class, prefUnit, intl)}
                  </b>
                  | Max:{' '}
                  <b data-test={`reference-range-max-${state.measurement_class}`} className="ms-2">
                    {getFormattedValueForMeasurementClass(
                      measurementLimit?.max,
                      state.measurement_class
                    )}{' '}
                    {prefUnit &&
                      getUnitForMeasurementClass(state.measurement_class, prefUnit, intl)}
                  </b>
                </div>
              )}
            </>
          )}
          {loading && <Loading className="mt-4" />}
          {error && <Error className="mt-4" retry={handleSubmit} dismissible={false} />}
        </Modal.Body>
        <Modal.Footer>
          {state.measurement_class && (
            <Button
              data-test="careplan-reference-ranges-modal-reset-btn"
              variant="danger"
              className="me-auto"
              onClick={handleReset}
            >
              {intl.formatMessage({ id: 'resetKey' })}
            </Button>
          )}
          <Button
            data-test="careplan-reference-ranges-modal-close-btn"
            variant="link"
            onClick={() => setModal(false)}
          >
            {intl.formatMessage({ id: 'Cancel' })}
          </Button>
          <Button
            data-test="careplan-reference-ranges-modal-submit-btn"
            variant="primary"
            disabled={!isSubmittable}
            onClick={handleSubmit}
          >
            {intl.formatMessage({ id: referenceRange ? 'saveButtonTitle' : 'add' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddEditReferenceRange;
